// Orden de las puertas de un módulo. Lo comparten el arrastre del Preview y el select de columna
// del formulario de puertas, así las dos vías reordenan y registran los cambios igual.

export const isSameDoor = (a, b) => (a?.id || a?.boxitDoorId) === (b?.id || b?.boxitDoorId);

/**
 * Orden físico de las puertas del locker: por módulo y, dentro del módulo, por doorSequence (doorOrder
 * en BD). No por doorNumber: al mover una puerta creada solo cambia su doorSequence, su número se conserva.
 * Lo usan la configuración y la vista del locker (SmartLocker), así las dos muestran el mismo orden.
 */
export const compareDoorPosition = (a, b) =>
  (Number(a.moduleNumber) || 0) - (Number(b.moduleNumber) || 0) ||
  (Number(a.doorSequence) || 0) - (Number(b.doorSequence) || 0) ||
  (Number(a.doorNumber) || 0) - (Number(b.doorNumber) || 0);

/** Columna e índice de una puerta dentro de su módulo. */
export const locateDoor = (module, door) => {
  for (const column of module?.columns || []) {
    const index = (column.doors || []).findIndex(d => isSameDoor(d, door));
    if (index !== -1) return { columnId: column.columnId, index };
  }
  return null;
};

/**
 * Mueve una puerta dentro de su módulo y recalcula el doorSequence (doorOrder) de todas,
 * columna por columna y de arriba hacia abajo. A los borradores también les recalcula el
 * doorNumber; las puertas creadas lo conservan.
 * `toIndex` es el índice en la columna destino antes de sacar la puerta (como al soltarla).
 * Devuelve la config nueva y las puertas guardadas que cambiaron de doorSequence.
 */
export const moveDoorInModule = (currentConfig, { moduleId, fromColumnId, fromIndex, toColumnId, toIndex }) => {
  const unchanged = { config: currentConfig, changedRealDoors: [] };
  if (!currentConfig?.modules) return unchanged;

  const config = JSON.parse(JSON.stringify(currentConfig));
  const module = config.modules.find(m => m.moduleId === moduleId);
  if (!module?.columns?.length) return unchanged;

  const fromColumn = module.columns.find(c => c.columnId === fromColumnId);
  const toColumn = module.columns.find(c => c.columnId === toColumnId);
  if (!fromColumn || !toColumn) return unchanged;

  const previousSequences = {};
  module.columns.forEach(col => col.doors.forEach(door => {
    if (door.boxitDoorId > 0) previousSequences[door.boxitDoorId] = door.doorSequence;
  }));

  const [movedDoor] = fromColumn.doors.splice(fromIndex, 1);
  if (!movedDoor) return unchanged;

  // dentro de la misma columna hacia abajo, el índice corre uno al sacar la puerta
  const insertIndex = fromColumnId === toColumnId && fromIndex < toIndex ? toIndex - 1 : toIndex;
  movedDoor.columnModule = toColumnId;
  toColumn.doors.splice(insertIndex, 0, movedDoor);

  const startDoorNumber = Number(module.startDoorNumber) || 1;
  let order = 1;
  module.columns
    .slice()
    .sort((a, b) => (a.columnId || 0) - (b.columnId || 0))
    .forEach(col => col.doors.forEach(door => {
      door.doorSequence = order++;
      if (!door.boxitDoorId) door.doorNumber = startDoorNumber + door.doorSequence - 1;
    }));

  const changedRealDoors = [];
  module.columns.forEach(col => col.doors.forEach(door => {
    const oldSequence = previousSequences[door.boxitDoorId];
    if (door.boxitDoorId > 0 && oldSequence != null && oldSequence !== door.doorSequence) {
      changedRealDoors.push({ ...door, oldSequence, newSequence: door.doorSequence });
    }
  }));

  return { config, changedRealDoors };
};
